import { useEffect, useState, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { apiClient } from 'core/http'
import socketClient from 'core/socket/socketClient'
import { Milestones } from '../milestones'
import friendsIcon from './assets/images/friends-icon-new.png'
import topDecoration from './assets/images/top-decoration.svg'
import c from './referral-content.module.scss'

const MILESTONE_THRESHOLDS = [3, 5, 7, 9, 12, 15, 18, 22, 25, 30, 40, 50, 60, 70, 80, 90, 100]

export const ReferralContent = () => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const [stats, setStats] = useState({ total: 0, reward: 0 })
    const [referralCode, setReferralCode] = useState('')

    const { remaining, progress } = useMemo(() => {
        const next = MILESTONE_THRESHOLDS.find((t) => t > stats.total) || MILESTONE_THRESHOLDS[MILESTONE_THRESHOLDS.length - 1]
        const prev = [...MILESTONE_THRESHOLDS].reverse().find((t) => t <= stats.total) || 0
        const rem = Math.max(0, next - stats.total)
        const pct = next > prev
            ? Math.min(100, ((stats.total - prev) / (next - prev)) * 100)
            : 100
        return { remaining: rem, progress: pct }
    }, [stats.total])

    const load = useCallback(async () => {
        try {
            const res = await apiClient.get('/friends')
            if (res?.data?.success) {
                setStats({
                    total: res.data.stats?.total ?? res.data.friends?.length ?? 0,
                    reward: res.data.stats?.totalReward ?? 0,
                })
            }
        } catch (e) {}
    }, [])

    useEffect(() => {
        load()
        ;(async () => {
            try {
                const res = await apiClient.get('/friends/referral-code')
                if (res?.data?.success && res.data.referralCode) {
                    setReferralCode(res.data.referralCode)
                }
            } catch (e) {}
        })()
        const handler = () => load()
        socketClient.on('referral_added', handler)
        return () => socketClient.off('referral_added', handler)
    }, [load])

    const handleInvite = async () => {
        const BOT_USERNAME = 'gg_funny_bot'
        const code = referralCode || '12345678'
        const deepLink = `https://t.me/${BOT_USERNAME}?startapp=${code}`
        const text = t('friends.banner.shareText')

        try {
            const currentLang = window.localStorage.getItem('i18nextLng') || 'ru'
            const webApp = window?.Telegram?.WebApp
            if (webApp?.shareMessage && webApp?.version && parseFloat(webApp.version) >= 8.0) {
                const response = await apiClient.post('/friends/prepare-share', {
                    language: currentLang
                })
                if (response?.data?.success && response?.data?.preparedMessageId) {
                    webApp.shareMessage(response.data.preparedMessageId, (sent) => {
                        if (sent) {
                            toast(t('tasks.banner.messageSent'), { type: 'success' })
                        }
                    })
                    return
                }
            }
        } catch (e) {
            console.log('prepareShare fallback:', e?.message)
        }

        try {
            const shareLink = `https://t.me/share/url?url=${encodeURIComponent(deepLink)}&text=${encodeURIComponent(text)}`
            if (window?.Telegram?.WebApp?.openTelegramLink) {
                window.Telegram.WebApp.openTelegramLink(shareLink)
            } else {
                await navigator.clipboard.writeText(`${text}\n${deepLink}`)
                toast(t('tasks.banner.linkCopied'), { type: 'success' })
            }
        } catch (e) {
            await navigator.clipboard.writeText(`${text}\n${deepLink}`)
            toast(t('tasks.banner.linkCopied'), { type: 'success' })
        }
    }

    return (
        <div className={c.component}>
            <h3 className={c.title}>{t('earnings.invite.title')}</h3>
            <p className={c.subtitle}>{t('earnings.invite.subtitle')}</p>

            <div className={c.banner}>
                <svg className={c.bannerBg} viewBox="0 0 354 212" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
                    <mask id="bannerMask" fill="white">
                        <path d="M343.473 0C345.089 0 346.637 0.652 347.766 1.808L352.293 6.445C353.387 7.566 354 9.07 354 10.637V206C354 209.314 351.314 212 348 212H10.06C8.444 212 6.897 211.348 5.768 210.192L1.707 206.033C0.613 204.913 0 203.408 0 201.842V6C0 2.686 2.686 0 6 0H343.473Z"/>
                    </mask>
                    <path d="M343.473 0C345.089 0 346.637 0.652 347.766 1.808L352.293 6.445C353.387 7.566 354 9.07 354 10.637V206C354 209.314 351.314 212 348 212H10.06C8.444 212 6.897 211.348 5.768 210.192L1.707 206.033C0.613 204.913 0 203.408 0 201.842V6C0 2.686 2.686 0 6 0H343.473Z" fill="url(#bannerFill)" fillOpacity="0.66"/>
                    <path d="M347.766 1.808L346.335 3.206L350.862 7.842L352.293 6.445L353.724 5.048L349.197 0.411L347.766 1.808ZM354 10.637H352V206H354H356V10.637H354ZM348 212V210H10.06V212V214H348V212ZM5.768 210.192L7.199 208.795L3.138 204.636L1.707 206.033L0.276 207.431L4.337 211.589L5.768 210.192ZM0 201.842H2V6H0H-2V201.842H0ZM6 0V2H343.473V0V-2H6V0ZM0 6H2C2 3.791 3.791 2 6 2V0V-2C1.582-2-2 1.582-2 6H0ZM1.707 206.033L3.138 204.636C2.408 203.889 2 202.886 2 201.842H0H-2C-2 203.93-1.183 205.936 0.276 207.431L1.707 206.033ZM10.06 212V210C8.983 210 7.951 209.565 7.199 208.795L5.768 210.192L4.337 211.589C5.842 213.131 7.906 214 10.06 214V212ZM354 206H352C352 208.209 350.209 210 348 210V212V214C352.418 214 356 210.418 356 206H354ZM352.293 6.445L350.862 7.842C351.592 8.59 352 9.592 352 10.637H354H356C356 8.548 355.183 6.542 353.724 5.048L352.293 6.445ZM347.766 1.808L349.197 0.411C347.691-1.131 345.628-2 343.473-2V0V2C344.55 2 345.582 2.435 346.335 3.206L347.766 1.808Z" fill="url(#bannerStroke)" mask="url(#bannerMask)"/>
                    <defs>
                        <linearGradient id="bannerFill" x1="177" y1="0" x2="177" y2="146.378" gradientUnits="userSpaceOnUse">
                            <stop stopColor="#FF371D" stopOpacity="0.44"/>
                            <stop offset="1" stopColor="#FF371D"/>
                        </linearGradient>
                        <linearGradient id="bannerStroke" x1="177" y1="0" x2="177" y2="146.378" gradientUnits="userSpaceOnUse">
                            <stop stopColor="#DA4429" stopOpacity="0.35"/>
                            <stop offset="1" stopColor="#FF4A32"/>
                        </linearGradient>
                    </defs>
                </svg>

                <img className={c.topDecoration} src={topDecoration} alt="" />

                <div className={c.topRow}>
                    <div className={c.friendsCount}>
                        <img className={c.friendsIcon} src={friendsIcon} alt="" />
                        <div className={c.friendsInfo}>
                            <span className={c.friendsNumber}>{stats.total}</span>
                            <span className={c.friendsLabel}>{t('earnings.milestones.friends')}</span>
                        </div>
                    </div>
                    <button className={c.howToInviteBtn} type="button" onClick={() => navigate('/faq?category=referrals')}>
                        {t('earnings.invite.howToInvite')}
                    </button>
                </div>

                <div className={c.progressWrap}>
                    <svg className={c.progressSvg} viewBox="0 0 306 14" fill="none" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
                        <defs>
                            <clipPath id="progressClip">
                                <path d="M4.21 4.599C6.484 1.696 9.968 0 13.656 0H290.908C294.052 0 297.071 1.234 299.314 3.437L304.837 8.859C306.754 10.741 305.422 14 302.736 14H3.006C0.504 14-0.899 11.119 0.644 9.15L4.21 4.599Z"/>
                            </clipPath>
                            <linearGradient id="progressGrad" x1="0" y1="7" x2="306" y2="7" gradientUnits="userSpaceOnUse">
                                <stop stopColor="#FFAA4A"/>
                                <stop offset="1" stopColor="#FF4144"/>
                            </linearGradient>
                            <pattern id="progressStripes" patternUnits="userSpaceOnUse" width="13.68" height="14">
                                <rect x="9.84" y="-6" width="4" height="30" transform="rotate(38.157 9.84 -6)" fill="#2E0603" fillOpacity="0.1"/>
                            </pattern>
                        </defs>
                        <path d="M4.21 4.599C6.484 1.696 9.968 0 13.656 0H290.908C294.052 0 297.071 1.234 299.314 3.437L304.837 8.859C306.754 10.741 305.422 14 302.736 14H3.006C0.504 14-0.899 11.119 0.644 9.15L4.21 4.599Z" fill="#250D0D" fillOpacity="0.3"/>
                        <g clipPath="url(#progressClip)" style={{ transform: `scaleX(${Math.max(0, progress) / 100})`, transformOrigin: 'left center', transition: 'transform 0.4s ease' }}>
                            <rect x="0" y="0" width="306" height="14" fill="url(#progressGrad)" />
                            <rect x="0" y="0" width="306" height="14" fill="url(#progressStripes)" />
                        </g>
                    </svg>
                </div>

                <span className={c.progressText}>
                    {t('earnings.invite.remaining', { count: remaining })}
                </span>

                <button className={c.inviteBtn} onClick={handleInvite}>
                    {t('earnings.invite.inviteFriend')}
                </button>
            </div>

            <Milestones totalFriends={stats.total} />
        </div>
    )
}
